/**
 * certificatePdf — builds a downloadable PDF for a recognition certificate,
 * fully client-side (no backend round-trip). A styled certificate face is
 * rendered into an off-screen DOM node, rasterized with html2canvas, then
 * placed onto an A4-landscape jsPDF page and saved.
 *
 * The certificate is a platform-issued recognition of community contribution
 * (NOT government accreditation) — labelled as such on the face.
 */
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import type { Certificate } from '../services/certificateService'
import { formatCertificateDate } from './utils'

function escapeHtml(s: string | undefined | null): string {
  if (!s) return ''
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;')
}

/**
 * Render and download a certificate as a PDF. `cert` is the full certificate
 * record (the volunteer's own view includes their name + the award title).
 */
export async function downloadCertificatePdf(cert: Certificate): Promise<void> {
  const verifyUrl = `${window.location.origin}/api/certificates/verify/${encodeURIComponent(cert.certificateNumber)}`

  // Build an off-screen certificate DOM node. Fixed width in px so html2canvas
  // produces a crisp, predictable raster; the PDF scales it to A4 landscape.
  const node = document.createElement('div')
  node.style.position = 'fixed'
  node.style.left = '-10000px'
  node.style.top = '0'
  node.style.width = '1000px'
  node.style.background = '#ffffff'
  node.style.padding = '56px 64px'
  node.style.fontFamily = "'Georgia', 'Times New Roman', serif"
  node.style.color = '#1f2937'
  node.style.border = '14px solid #1d4ed8'
  node.style.borderRadius = '8px'
  node.style.boxSizing = 'border-box'
  node.innerHTML = `
    <div style="text-align:center; margin-bottom:8px;">
      <div style="display:inline-flex; align-items:center; justify-content:center; width:64px; height:64px; border-radius:12px; background:linear-gradient(135deg,#2563eb,#06b6d4); margin-bottom:12px;">
        <span style="color:#fff; font-weight:700; font-size:22px; font-family:Arial,sans-serif;">TB</span>
      </div>
      <div style="font-size:18px; letter-spacing:6px; color:#1d4ed8; text-transform:uppercase; font-family:Arial,sans-serif;">TB Mukt Bharat</div>
      <div style="font-size:13px; color:#6b7280; margin-top:2px; font-family:Arial,sans-serif;">Community Recognition Programme</div>
    </div>
    <div style="text-align:center; margin:24px 0 8px;">
      <div style="font-size:34px; font-weight:700; color:#111827;">Certificate of Recognition</div>
      <div style="font-size:13px; color:#6b7280; margin-top:6px;">This is to proudly recognise the contribution of</div>
    </div>
    <div style="text-align:center; margin:18px 0;">
      <div style="font-size:30px; font-weight:700; color:#1d4ed8; border-bottom:2px solid #cbd5e1; display:inline-block; padding:0 28px 8px;">${escapeHtml(cert.volunteerName)}</div>
    </div>
    <div style="text-align:center; margin:8px auto; max-width:720px; font-size:16px; line-height:1.6; color:#374151;">
      for earning the <strong>${escapeHtml(cert.awardTitle)}</strong> recognition${cert.programmeTag ? ` under the <em>${escapeHtml(cert.programmeTag)}</em> programme` : ''}.
      ${cert.description ? `<div style="margin-top:10px; font-size:14px; color:#4b5563;">${escapeHtml(cert.description)}</div>` : ''}
    </div>
    <div style="display:flex; justify-content:space-between; margin-top:56px; padding-top:20px; border-top:1px solid #e5e7eb; font-family:Arial,sans-serif;">
      <div>
        <div style="font-size:15px; font-weight:600; color:#111827;">${escapeHtml(cert.issuedByName) || 'TB Mukt Bharat'}</div>
        <div style="font-size:12px; color:#6b7280;">Issued by</div>
      </div>
      <div style="text-align:center;">
        <div style="font-size:14px; color:#111827;">${cert.issueDate ? formatCertificateDate(cert.issueDate) : ''}</div>
        <div style="font-size:12px; color:#6b7280;">Date of issue</div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:14px; color:#111827; font-family:monospace;">${escapeHtml(cert.certificateNumber)}</div>
        <div style="font-size:12px; color:#6b7280;">Certificate No.</div>
      </div>
    </div>
    <div style="text-align:center; margin-top:20px; font-size:11px; color:#9ca3af; font-family:Arial,sans-serif;">
      Verify online at ${escapeHtml(verifyUrl)} &middot; This is a platform-issued recognition of community contribution and implies no government accreditation.
    </div>
  `
  document.body.appendChild(node)

  try {
    const canvas = await html2canvas(node, { scale: 2, backgroundColor: '#ffffff', useCORS: true })
    const imgData = canvas.toDataURL('image/png')

    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    // Fit the canvas to the page with a small margin, preserving aspect ratio.
    const margin = 10
    const availW = pageWidth - margin * 2
    const availH = pageHeight - margin * 2
    const imgRatio = canvas.width / canvas.height
    let drawW = availW
    let drawH = drawW / imgRatio
    if (drawH > availH) {
      drawH = availH
      drawW = drawH * imgRatio
    }
    const x = (pageWidth - drawW) / 2
    const y = (pageHeight - drawH) / 2
    pdf.addImage(imgData, 'PNG', x, y, drawW, drawH)
    pdf.save(`certificate-${cert.certificateNumber}.pdf`)
  } finally {
    document.body.removeChild(node)
  }
}